<template>
  <PopOverMenu
    class="notification-menu"
    :offset="15"
    placement="right"
  >
    <template #target="{ isOpen }">
      <IconButton
        class="notification-menu__target"
        flair="rounded"
        kind="transparent"
        size="sm"
      >
        <ToolTip :content="`${isOpen ? 'Hide' : 'Show'} notifications`">
          <Icon
            name="mono-notification-bell"
            class="notification-menu__target-icon"
            :class="{ 'notification-menu__target-icon--unread': hasUnreadNotifications }"
          />
        </ToolTip>
      </IconButton>
    </template>
    <template #menu="{ closeMenu }">
      <div class="notification-menu__content">
        <div class="notification-menu__content-header">
          <div class="notification-menu__content-header-title">Team Notifications</div>
          <IconButton
            class="notification-menu__content-header-settings"
            flair="rounded"
            kind="transparent"
            size="sm"
            aria-label="Open notification settings"
            @click="
              () => {
                openNotificationSettings()
                closeMenu()
              }
            "
          >
            <ToolTip
              content="Open personal info settings"
              placement="left"
            >
              <Icon
                class="notification-menu__content-header-icon"
                name="duotone-settings"
              />
            </ToolTip>
          </IconButton>
        </div>
        <div
          v-if="notifications.length > 0"
          class="notification-menu__content-notification-items"
        >
          <NotificationItem
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item"
            :notification="notification"
            :is-oldest="!!oldestNotification && notification.id === oldestNotification.id"
            @close="closeMenu"
          />
        </div>
        <div
          v-else
          class="notification-menu__content-no-items"
        >
          No notification found.
        </div>
      </div>
    </template>
  </PopOverMenu>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useTeamStore } from '@/pinia/useTeamStore'
import type { NotificationPayload } from '@/store/types/NotificationPayload'
import Icon from '@/uiKit/Icon/Icon.vue'
import IconButton from '@/uiKit/Button/IconButton.vue'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import { useNotificationsStore } from './useNotificationsStore'
import NotificationItem from './NotificationItem.vue'
import PopOverMenu from '@/uiKit/PopOverMenu.vue'
import { useRouter } from 'vue-router'

const notificationsStore = useNotificationsStore()
const teamStore = useTeamStore()

const currentTeamId = computed(() => teamStore.currentTeam?.id)

const unreadCountForTeam = computed<number>(
  () => notificationsStore.byTeamUnreadCount[currentTeamId.value ?? -1] || 0,
)

const hasUnreadNotifications = computed(() => unreadCountForTeam.value > 0)

const notifications = computed<NotificationPayload[]>(() =>
  // notifications are already kept sorted by the store
  currentTeamId.value
    ? notificationsStore.sortedNotifications.filter((n) => n.team_id === currentTeamId.value)
    : [],
)

const oldestNotification = computed<NotificationPayload | undefined>(
  () => notifications.value[notifications.value.length - 1],
)

const router = useRouter()

const openNotificationSettings = (): void => {
  router.push({ query: { ...router.currentRoute.value.query, settings: 'profile' } })
}
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/heights.scss';
@import '@/uiKit/assets/spacings.scss';
@import '@/assets/styles/shared/scrollbarV2';

.notification-menu {
  overflow: hidden;

  &__target {
    &-icon {
      font-size: $h-icon;
      color: var(--color-icon-subtlest);

      &--unread {
        &:after {
          content: '';
          position: absolute;
          top: 10px;
          right: 6px;
          transform: translate(-50%, -50%);
          width: 5px;
          height: 5px;
          background-color: var(--color-background-warning);
          border-radius: 100%;
          outline: 1px solid var(--color-surface-tertiary);
        }
      }
    }
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 360px;
    height: 480px;
    padding: 0;
    box-shadow: $effectShadowsMD;
    overflow: hidden;

    &-header {
      position: relative;
      display: inline-flex;
      justify-content: space-between;
      align-items: center;
      height: $h-xl;
      width: 100%;
      padding: $s-xl $s-md $s-xl $s-xl;
      background-color: var(--color-background-gray-subtlest);

      &-title {
        color: var(--color-text-default);
        @include typographyRegularBody400;
        font-weight: 600;
      }

      &-icon {
        height: $h-icon;
        width: $h-icon;
        font-size: $h-icon;
        color: var(--color-text-subtle);
      }
    }

    &-notification-items {
      position: relative;
      display: inline-flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: $s-xs;
      width: 100%;
      height: 100%;
      padding: 0 $s-xs;
      @include scrollbarV2;

      .notification-item {
        width: 100%;
      }
    }

    &-no-items {
      position: relative;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      padding: $s-xl;
      height: 100%;
      width: 100%;
      @include typographyRegularBody200;
      color: var(--color-text-subtlest);
    }
  }

  :deep(.popup-menu) {
    padding: 0;
  }
}
</style>
